import React, { useState, useEffect } from "react";
import styles from "./style.module.scss";
import TextInput from "../../../../../../components/form/text-input";
import Select from "../../../../../../components/form/select";
import { US_STATES } from "../../../../../../../constants";
import CaretDown from "../../../../../../assets/svg/icons/common/CaretDown";
import baseStyles from '../../base.module.scss';
import FancyRadio from "../../../../../../components/form/fancy-radio-input";
import HeadingWithLine from "../../../../../../components/HeadingWithLine/HeadingWithLine";
import InfoIcon from "../../../../../../assets/svg/icons/common/info";
import Button from '../../../../../../components/new-button'
import PlusIcon from "../../../../../../assets/svg/icons/common/Plus";
import { getCountries } from "../../../../../../../api";
import Switch from "../../../../../../components/form/switch";
import useSalesFormStore from "../../../../../../stores/sales-form";
import AddressAutocomplete from "../../../../../../components/form/address-autocomplete";
import { googlePlaceToFormData } from "../../../../../../utils/address-parser";
import ReactTooltip from "react-tooltip";
import NumberInput from "../../../../../../components/form/number-input";

const idTypeOptions = [
  { value: "LOCAL_STATE_ID", label: "Driver's License" },
  { value: "PASSPORT", label: "Passport" },
  { value: "STATE_ID", label: "State ID" }
];

const ToolTipSocialSecurity = "Do not add dashes. Please make sure your credit isn’t frozen or blocked before submitting this form.";
const ToolTipBirthDate = "You must be 18 years or older to apply";
const ToolTipOwnershipPercentage = "Only people with at least 25% ownership need to be included on this application.";

/**
 * PersonalInformation Component
 * Renders a form for collecting personal information including contact details, address, and additional owners
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formik - Formik form instance containing values, errors, and handlers
 */
const PersonalInformation = ({ formik }) => {
  const { values, errors, touched, handleChange, setFieldValue } = formik;
  const [usStateList, setUsStateList] = useState([]);
  const [additionalOwnersOpen, setAdditionalOwnersOpen] = useState({});
  const [countries, setCountries] = useState([]);

  /**
   * Initialize US state list options on component mount
   */
  useEffect(() => {
    const stateOptions = Object.entries(US_STATES).map(([key, value]) => ({
      label: value,
      value: value
    }));
    setUsStateList(stateOptions);

    getCountries().then((res) => {
      setCountries(res.data);
    });
  }, []);

  /**
   * Handles the "Same as" radio button changes
   * Copies address information based on selection
   * @param {string} value - Selected option ('same_as_no', 'same_as_dba', or 'same_as_legal')
   */
  const handleSameAsChange = (value) => {
    setFieldValue('personal_same_as_dba_legal', value);

    if (value === 'dba') {
      setFieldValue('field_personalstreet', values.dba_address);
      setFieldValue('field_personalcity', values.dba_city);
      // Find the state by name from our options list
      const stateValue = values.dba_state ? US_STATES[values.dba_state] || values.dba_state : "";
      setFieldValue('field_personalstate', stateValue);
      setFieldValue('field_personalzip', values.dba_zip);
      setFieldValue('personal_phone', values.business_phone);
      setFieldValue('personal_email', values.business_email);

    } else if (value === 'business') {
      setFieldValue('field_personalstreet', values.legal_address);
      setFieldValue('field_personalcity', values.legal_city);
      // Find the state by name from our options list
      const stateValue = values.legal_state ? US_STATES[values.legal_state] || values.legal_state : "";
      setFieldValue('field_personalstate', stateValue);
      setFieldValue('field_personalzip', values.legal_zip);
      setFieldValue('personal_phone', values.legal_phone);
      setFieldValue('personal_email', values.business_email);
      setFieldValue('field_residenceyears', values.location_years);
      setFieldValue('field_residencemonths', values.location_months);
    } else if (value === 'no') {
      // Clear all fields when user switches to "No" option
      setFieldValue('field_personalstreet', '');
      setFieldValue('field_personalcity', '');
      setFieldValue('field_personalstate', '');
      setFieldValue('field_personalzip', '');
      setFieldValue('personal_phone', '');
      setFieldValue('personal_email', '');
      setFieldValue('field_residenceyears', '');
      setFieldValue('field_residencemonths', '');
    }
  };

  /**
   * Toggles the visibility of an additional owner's details section
   * @param {number} index - Index of the owner to toggle
   */
  const toggleAdditionalOwner = (index) => {
    setAdditionalOwnersOpen(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  /**
   * Adds a new owner to the form
   */
  const addOwner = () => {
    const newOwner = {
      signer_first_name: "",
      signer_last_name: "",
      signer_email: "",
      signer_phone: "",
      signer_address: "",
      signer_city: "",
      signer_state: "",
      signer_zip: "",
      signer_country: "",
      signer_social_security: "",
      signer_drivers_license_number: "",
      signer_drivers_license_state: "",
      signer_date_of_birth: "",
      signer_document_type: "",
      signer_document_country: "",
      signer_ownership_percentage: "",
      signer_residence_years: "",
      signer_residence_months: ""
    };
    setFieldValue('additional_owners', [
      ...(values.additional_owners || []),
      newOwner
    ]);
    setAdditionalOwnersOpen(prev => ({
      ...prev,
      [values.additional_owners?.length || 0]: true
    }));
  };

  /**
   * Removes an owner from the form
   * @param {number} index - Index of the owner to remove
   */
  const removeOwner = (index) => {
    const updatedOwners = [...values.additional_owners];
    updatedOwners.splice(index, 1);
    setFieldValue('additional_owners', updatedOwners);

    const newOpenStates = {};
    Object.keys(additionalOwnersOpen).forEach(key => {
      const keyNum = parseInt(key);
      if (keyNum < index) {
        newOpenStates[keyNum] = additionalOwnersOpen[keyNum];
      } else if (keyNum > index) {
        newOpenStates[keyNum - 1] = additionalOwnersOpen[keyNum];
      }
    });
    setAdditionalOwnersOpen(newOpenStates);
  };

  // Add function to handle address selection from Google Place Autocomplete
  const handlePersonalAddressSelected = (place) => {
    if (values.personal_same_as_dba_legal === 'dba' || values.personal_same_as_dba_legal === 'business') {
      return; // Don't handle if using DBA or Legal address
    }

    // Parse the Google place data
    const addressData = googlePlaceToFormData(place, {
      streetAddress: 'field_personalstreet',
      city: 'field_personalcity',
      state: 'field_personalstate',
      zipCode: 'field_personalzip'
    });

    // Special handling for state to ensure it matches the dropdown options
    if (addressData.field_personalstate) {
      // Find matching state in the state list options
      const matchingState = usStateList.find(option =>
        option.label === addressData.field_personalstate ||
        option.value === addressData.field_personalstate
      );

      if (matchingState) {
        addressData.field_personalstate = matchingState.value;
      } else {
        console.log('No matching state found in dropdown for:', addressData.field_personalstate);
      }
    }

    // Update the form fields
    Object.entries(addressData).forEach(([field, value]) => {
      setFieldValue(field, value);
    });
  };

  // Function to handle address selection for additional owners
  const handleAdditionalOwnerAddressSelected = (place, index) => {
    // Parse the Google place data
    const addressData = googlePlaceToFormData(place, {
      address: 'address',
      city: 'city',
      state: 'state',
      zipCode: 'zip'
    });

    // Special handling for state to ensure it matches the dropdown options
    if (addressData.state) {
      // Find matching state in the state list options
      const matchingState = usStateList.find(option =>
        option.label === addressData.state ||
        option.value === addressData.state
      );

      if (matchingState) {
        addressData.state = matchingState.value;
      } else {
        console.log('No matching state found in dropdown for:', addressData.state);
      }
    }

    // Update the nested form fields for the owner at the given index
    Object.entries(addressData).forEach(([field, value]) => {
      const ownerField = `additional_owners.${index}.signer_${field}`;
      setFieldValue(ownerField, value);
    });
  };

  return (
    <div className={styles.container}>
      <ReactTooltip className={styles.tooltip} />

      <div className={styles.header}>
        <h6 className={styles.title}>Personal Information</h6>
      </div>

      <div className={styles.content}>

        <div className={styles.radioGroupContainer}>
          <div className={styles.radioGroupTitle}>
            <p className={styles.subtitle}>Personal Information the same as your DBA or Legal</p>
          </div>

          <div className={styles.radioGroup}>
            <FancyRadio
              label="No"
              name="personal_same_as_dba_legal"
              id="personal_same_as_no"
              checked={values.personal_same_as_dba_legal === 'no'}
              onChange={() => handleSameAsChange('no')}
              error={touched.personal_same_as_dba_legal && errors.personal_same_as_dba_legal}
            />

            <FancyRadio
              label="Same as DBA"
              name="personal_same_as_dba_legal"
              id="personal_same_as_dba"
              checked={values.personal_same_as_dba_legal === 'dba'}
              onChange={() => handleSameAsChange('dba')}
              error={touched.personal_same_as_dba_legal && errors.personal_same_as_dba_legal}
            />

            <FancyRadio
              label="Same as Legal"
              name="personal_same_as_dba_legal"
              id="personal_same_as_legal"
              checked={values.personal_same_as_dba_legal === 'business'}
              onChange={() => handleSameAsChange('business')}
              error={touched.personal_same_as_dba_legal && errors.personal_same_as_dba_legal}
            />
          </div>
          {touched.personal_same_as_dba_legal && errors.personal_same_as_dba_legal && (
            <div className={styles.error}>{errors.personal_same_as_dba_legal}</div>
          )}
        </div>

        {/* Name Fields */}
        <div className={styles.formGroup}>
          <TextInput
            label={"First Name"}
            placeholder={"First Name"}
            type="text"
            id="personal_first_name"
            name="personal_first_name"
            value={values.personal_first_name}
            onChange={handleChange}
            touched={touched.personal_first_name}
            errored={errors.personal_first_name}
            error={errors.personal_first_name}
          />

          <TextInput
            label={"Last Name"}
            placeholder={"Last Name"}
            type="text"
            id="personal_last_name"
            name="personal_last_name"
            value={values.personal_last_name}
            onChange={handleChange}
            touched={touched.personal_last_name}
            errored={errors.personal_last_name}
            error={errors.personal_last_name}
          />
        </div>

        {/* Phone and Email */}
        <div className={styles.formGroup}>
          <TextInput
            label={"Personal Phone"}
            placeholder={"Personal Phone"}
            type="text"
            id="personal_phone"
            name="personal_phone"
            value={values.personal_phone}
            touched={touched.personal_phone}
            errored={errors.personal_phone}
            error={errors.personal_phone}
            disabled={values.personal_same_as_dba_legal === 'dba' || values.personal_same_as_dba_legal === 'business'}
            cleaveOptions={{
              blocks: [3, 3, 4],
              delimiter: "-",
              numericOnly: true,
              onValueChanged: (e) => {
                handleChange({
                  target: { name: "personal_phone", value: e.target.value },
                });
              },
            }}
          />

          <TextInput
            label={"Personal Email"}
            placeholder={"Personal Email"}
            type="text"
            id="personal_email"
            name="personal_email"
            value={values.personal_email}
            onChange={handleChange}
            touched={touched.personal_email}
            errored={errors.personal_email}
            error={errors.personal_email}
            disabled={values.personal_same_as_dba_legal === 'dba' || values.personal_same_as_dba_legal === 'business'}
          />
        </div>

        {/* Address */}
        <div className={styles.formGroup}>
          <AddressAutocomplete
            label={"Personal Street Address"}
            placeholder={"Personal Street Address"}
            id="field_personalstreet"
            name="field_personalstreet"
            value={values.field_personalstreet}
            onChange={handleChange}
            onPlaceSelected={handlePersonalAddressSelected}
            touched={touched.field_personalstreet}
            errored={errors.field_personalstreet}
            error={errors.field_personalstreet}
            disabled={values.personal_same_as_dba_legal === 'dba' || values.personal_same_as_dba_legal === 'business'}
          />
        </div>

        <div className={styles.formGroup}>
          <TextInput
            label={"City"}
            placeholder={"City"}
            type="text"
            id="field_personalcity"
            name="field_personalcity"
            value={values.field_personalcity}
            onChange={handleChange}
            touched={touched.field_personalcity}
            errored={errors.field_personalcity}
            error={errors.field_personalcity}
            disabled={values.personal_same_as_dba_legal === 'dba' || values.personal_same_as_dba_legal === 'business'}
          />

          <div>
            <Select
              label="State"
              placeholder="Select State"
              id="field_personalstate"
              name="field_personalstate"
              options={usStateList}
              search
              value={values.field_personalstate || ""}
              onChange={(value) => {
                setFieldValue("field_personalstate", value);
              }}
              error={errors.field_personalstate}
              touched={touched.field_personalstate}
              errored={errors.field_personalstate}
              disabled={values.personal_same_as_dba_legal === 'dba' || values.personal_same_as_dba_legal === 'business'}
            />
          </div>

          <TextInput
            label={"Zip"}
            placeholder={"Zip"}
            type="text"
            id="field_personalzip"
            name="field_personalzip"
            value={values.field_personalzip}
            touched={touched.field_personalzip}
            errored={errors.field_personalzip}
            error={errors.field_personalzip}
            disabled={values.personal_same_as_dba_legal === 'dba' || values.personal_same_as_dba_legal === 'business'}
            cleaveOptions={{
              blocks: [5],
              delimiter: "",
              numericOnly: true,
              onValueChanged: (e) => {
                handleChange({
                  target: { name: "field_personalzip", value: e.target.value },
                });
              },
            }}
          />
        </div>

        {/* Years of Residence */}
        <div className={styles.formGroup}>
          <div className={styles.residenceGroup}>
            <div className={`${baseStyles.grid} ${baseStyles.gridCol3} ${baseStyles.gap16px}`}>
              <NumberInput
                label="Years of Residence"
                placeholder="0"
                type="text"
                id="field_residenceyears"
                name="field_residenceyears"
                min={0}
                max={99}
                value={values.field_residenceyears}
                onChange={(value) => setFieldValue("field_residenceyears", value)}
                suffix="Years"
                error={errors.field_residenceyears}
                touched={touched.field_residenceyears}
                errored={errors.field_residenceyears}
              />

              <NumberInput
                label="Months of Residence"
                placeholder="0"
                type="text"
                id="field_residencemonths"
                name="field_residencemonths"
                min={0}
                max={11}
                value={values.field_residencemonths}
                onChange={(value) => setFieldValue("field_residencemonths", value)}
                suffix="Months"
                error={errors.field_residencemonths}
                touched={touched.field_residencemonths}
                errored={errors.field_residencemonths}
              />
            </div>
          </div>
        </div>

        {/* ID Document Section */}
        <div className={styles.documentSection}>
          <HeadingWithLine text="IDENTITY DOCUMENT" margin={false} />

          <div className={`${baseStyles.grid} ${baseStyles.gridCol3} ${baseStyles.gap16px}`} >
            <div>
              <Select
                label="ID Type"
                placeholder="ID Type"
                id="field_documenttype"
                name="field_documenttype"
                options={idTypeOptions}
                value={values?.field_documenttype}
                onChange={(value) => {
                  handleChange({ target: { name: "field_documenttype", value } });
                }}
              />
              {errors.field_documenttype && touched.field_documenttype && (
                <p className={styles.error}>{errors.field_documenttype}</p>
              )}
            </div>
            <TextInput
              label={"ID Number"}
              placeholder={"ID Number"}
              type="text"
              id="field_documentnumber"
              name="field_documentnumber"
              value={values.field_documentnumber}
              onChange={handleChange}
              touched={touched.field_documentnumber}
              errored={errors.field_documentnumber}
              error={errors.field_documentnumber}
            />

            {(values.field_documenttype === "LOCAL_STATE_ID" || values.field_documenttype === "STATE_ID") && (
              <div>
                <Select
                  label="State"
                  placeholder="Select State"
                  id="field_driverstate"
                  name="field_driverstate"
                  options={usStateList}
                  search
                  value={values.field_driverstate || ""}
                  onChange={(value) => {
                    setFieldValue("field_driverstate", value);
                  }}
                  error={errors.field_driverstate}
                  touched={touched.field_driverstate}
                  errored={errors.field_driverstate}
                />
              </div>
            )}

            {(values.field_documenttype === "PASSPORT") && (
              <div>
                <Select
                  label="Country"
                  placeholder="Select Country"
                  id="document_country"
                  name="document_country"
                  options={countries}
                  search
                  value={values.document_country || ""}
                  onChange={(value) => {
                    setFieldValue("document_country", value);
                  }}
                  error={errors.document_country}
                  touched={touched.document_country}
                  errored={errors.document_country}
                />
              </div>
            )}


          </div>
          <div className={`${baseStyles.grid} ${baseStyles.gridCol3} ${baseStyles.gap16px}`} >
            <TextInput
              label={"Social Security Number"}
              placeholder={"XXX-XX-XXXX"}
              type="text"
              id="field_socialsecurity"
              name="field_socialsecurity"
              value={values.field_socialsecurity}
              onChange={handleChange}
              touched={touched.field_socialsecurity}
              errored={errors.field_socialsecurity}
              error={errors.field_socialsecurity}
              suffix={<InfoIcon
                data-tip={ToolTipSocialSecurity}
                data-place="top"
              />}
              cleaveOptions={{
                blocks: [3, 2, 4],
                delimiter: "-",
                numericOnly: true,
                onValueChanged: (e) => {
                  handleChange({
                    target: { name: "field_socialsecurity", value: e.target.value },
                  });
                },
              }}
            />
            <TextInput
              label={"Date of Birth"}
              placeholder={"MM/DD/YYYY"}
              type="text"
              id="field_birthdate"
              name="field_birthdate"
              value={values.field_birthdate}
              onChange={handleChange}
              touched={touched.field_birthdate}
              errored={errors.field_birthdate}
              error={errors.field_birthdate}
              suffix={<InfoIcon
                data-tip={ToolTipBirthDate}
                data-place="top"
              />}
              cleaveOptions={{
                date: true,
                datePattern: ["m", "d", "Y"],
                delimiter: "/",
                blocks: [2, 2, 4],
                onValueChanged: (e) => {
                  // Ensure the value is properly formatted
                  let value = e.target.value;
                  if (value && value.length === 8) {
                    value = value.replace(/(\d{2})(\d{2})(\d{4})/, '$1/$2/$3');
                  }
                  setFieldValue("field_birthdate", value);
                },
              }}
            />

          </div>
        </div>

        {/* Ownership Section */}
        <div className={styles.documentSection}>
          <HeadingWithLine text="OWNERSHIP" margin={false} />

          <div className={`${baseStyles.grid} ${baseStyles.gridCol3} ${baseStyles.gap16px}`} >
            <NumberInput
              label="Ownership Percentage"
              placeholder="0"
              type="text"
              id="field_ownershippercentage"
              name="field_ownershippercentage"
              min={0}
              max={100}
              value={values.field_ownershippercentage}
              onChange={(value) => setFieldValue("field_ownershippercentage", value)}
              suffix={<>
                <InfoIcon 
                  data-tip={ToolTipOwnershipPercentage}
                  data-place="top"
                />
                <span className={baseStyles.ml8px}>%</span>
              </>}
              error={errors.field_ownershippercentage}
              touched={touched.field_ownershippercentage}
              errored={errors.field_ownershippercentage}
            />
          </div>
        </div>

        {/* Additional Owners */}
        {values.additional_owners && values.additional_owners.length > 0 &&
          values.additional_owners.map((owner, index) => (
            <div key={index} className={styles.additionalOwner}>
              <div
                className={styles.additionalOwnerHeader}
                onClick={() => toggleAdditionalOwner(index)}
              >
                <div className={styles.additionalOwnerTitle}>
                  <CaretDown
                    className={additionalOwnersOpen[index] ? styles.caretOpen : styles.caretClosed}
                  />
                  <span>ADDITIONAL OWNER #{index + 2}</span>
                </div>
                <button
                  className={styles.removeButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeOwner(index);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M13.5 3H11V2.5C11 2.10218 10.842 1.72064 10.5607 1.43934C10.2794 1.15804 9.89782 1 9.5 1H6.5C6.10218 1 5.72064 1.15804 5.43934 1.43934C5.15804 1.72064 5 2.10218 5 2.5V3H2.5C2.36739 3 2.24021 3.05268 2.14645 3.14645C2.05268 3.24021 2 3.36739 2 3.5C2 3.63261 2.05268 3.75979 2.14645 3.85355C2.24021 3.94732 2.36739 4 2.5 4H3V13C3 13.2652 3.10536 13.5196 3.29289 13.7071C3.48043 13.8946 3.73478 14 4 14H12C12.2652 14 12.5196 13.8946 12.7071 13.7071C12.8946 13.5196 13 13.2652 13 13V4H13.5C13.6326 4 13.7598 3.94732 13.8536 3.85355C13.9473 3.75979 14 3.63261 14 3.5C14 3.36739 13.9473 3.24021 13.8536 3.14645C13.7598 3.05268 13.6326 3 13.5 3ZM6 2.5C6 2.36739 6.05268 2.24021 6.14645 2.14645C6.24021 2.05268 6.36739 2 6.5 2H9.5C9.63261 2 9.75979 2.05268 9.85355 2.14645C9.94732 2.24021 10 2.36739 10 2.5V3H6V2.5ZM12 13H4V4H12V13ZM7 6.5V10.5C7 10.6326 6.94732 10.7598 6.85355 10.8536C6.75979 10.9473 6.63261 11 6.5 11C6.36739 11 6.24021 10.9473 6.14645 10.8536C6.05268 10.7598 6 10.6326 6 10.5V6.5C6 6.36739 6.05268 6.24021 6.14645 6.14645C6.24021 6.05268 6.36739 6 6.5 6C6.63261 6 6.75979 6.05268 6.85355 6.14645C6.94732 6.24021 7 6.36739 7 6.5ZM10 6.5V10.5C10 10.6326 9.94732 10.7598 9.85355 10.8536C9.75979 10.9473 9.63261 11 9.5 11C9.36739 11 9.24021 10.9473 9.14645 10.8536C9.05268 10.7598 9 10.6326 9 10.5V6.5C9 6.36739 9.05268 6.24021 9.14645 6.14645C9.24021 6.05268 9.36739 6 9.5 6C9.63261 6 9.75979 6.05268 9.85355 6.14645C9.94732 6.24021 10 6.36739 10 6.5Z" fill="#3A383F" />
                  </svg>
                </button>
              </div>

              {additionalOwnersOpen[index] && (
                <div className={styles.additionalOwnerContent}>
                  {/* Owner form fields would go here - similar to main form */}
                  <div className={styles.formGroup}>
                    <TextInput
                      label={"First Name"}
                      placeholder={"First Name"}
                      type="text"
                      id={`additional_owners.${index}.signer_first_name`}
                      name={`additional_owners.${index}.signer_first_name`}
                      value={owner.signer_first_name}
                      onChange={handleChange}
                      error={errors?.additional_owners?.[index]?.signer_first_name}
                      touched={touched?.additional_owners?.[index]?.signer_first_name}
                      errored={errors?.additional_owners?.[index]?.signer_first_name}
                    />

                    <TextInput
                      label={"Last Name"}
                      placeholder={"Last Name"}
                      type="text"
                      id={`additional_owners.${index}.signer_last_name`}
                      name={`additional_owners.${index}.signer_last_name`}
                      value={owner.signer_last_name}
                      onChange={handleChange}
                      error={errors?.additional_owners?.[index]?.signer_last_name}
                      touched={touched?.additional_owners?.[index]?.signer_last_name}
                      errored={errors?.additional_owners?.[index]?.signer_last_name}
                    />
                  </div>

                  <div className={`${baseStyles.grid} ${baseStyles.gridCol2} ${baseStyles.gap16px}`}>
                    <TextInput
                      label={"Personal Phone"}
                      placeholder={"Personal Phone"}
                      type="text"
                      id={`additional_owners.${index}.signer_phone`}
                      name={`additional_owners.${index}.signer_phone`}
                      value={owner.signer_phone}
                      error={errors?.additional_owners?.[index]?.signer_phone}
                      touched={touched?.additional_owners?.[index]?.signer_phone}
                      errored={errors?.additional_owners?.[index]?.signer_phone}
                      cleaveOptions={{
                        blocks: [3, 3, 4],
                        delimiter: "",
                        numericOnly: true,
                        onValueChanged: (e) => {
                          handleChange({
                            target: { name: `additional_owners.${index}.signer_phone`, value: e.target.value },
                          });
                        },
                      }}
                    />

                    <TextInput
                      label={"Personal Email"}
                      placeholder={"Personal Email"}
                      type="email"
                      id={`additional_owners.${index}.signer_email`}
                      name={`additional_owners.${index}.signer_email`}
                      value={owner.signer_email}
                      onChange={handleChange}
                      error={errors?.additional_owners?.[index]?.signer_email}
                      touched={touched?.additional_owners?.[index]?.signer_email}
                      errored={errors?.additional_owners?.[index]?.signer_email}
                    />
                  </div>

                  {/* Address */}
                  <div className={styles.addressSection}>
                    <AddressAutocomplete
                      label={"Street Address"}
                      placeholder={"Street Address"}
                      id={`additional_owners.${index}.signer_address`}
                      name={`additional_owners.${index}.signer_address`}
                      value={owner.signer_address}
                      onChange={handleChange}
                      onPlaceSelected={(place) => handleAdditionalOwnerAddressSelected(place, index)}
                    />
                  </div>

                  <div className={`${baseStyles.grid} ${baseStyles.gridCol3} ${baseStyles.gap16px}`}>
                    <TextInput
                      label={"City"}
                      placeholder={"City"}
                      type="text"
                      id={`additional_owners.${index}.signer_city`}
                      name={`additional_owners.${index}.signer_city`}
                      value={owner.signer_city}
                      onChange={handleChange}
                      error={errors?.additional_owners?.[index]?.signer_city}
                      touched={touched?.additional_owners?.[index]?.signer_city}
                      errored={errors?.additional_owners?.[index]?.signer_city}
                    />
                    <Select
                      label="State"
                      placeholder="Select State"
                      id={`additional_owners.${index}.signer_state`}
                      name={`additional_owners.${index}.signer_state`}
                      options={usStateList}
                      search
                      value={owner.signer_state || ""}
                      onChange={(value) => {
                        setFieldValue(`additional_owners.${index}.signer_state`, value);
                      }}
                      error={errors?.additional_owners?.[index]?.signer_state}
                      touched={touched?.additional_owners?.[index]?.signer_state}
                      errored={errors?.additional_owners?.[index]?.signer_state}
                    />

                    <TextInput
                      label={"Zip"}
                      placeholder={"Zip"}
                      type="text"
                      id={`additional_owners.${index}.signer_zip`}
                      name={`additional_owners.${index}.signer_zip`}
                      value={owner.signer_zip}
                      error={errors?.additional_owners?.[index]?.signer_zip}
                      touched={touched?.additional_owners?.[index]?.signer_zip}
                      errored={errors?.additional_owners?.[index]?.signer_zip}
                      cleaveOptions={{
                        blocks: [5],
                        delimiter: "",
                        numericOnly: true,
                        onValueChanged: (e) => {
                          handleChange({
                            target: { name: `additional_owners.${index}.signer_zip`, value: e.target.value },
                          });
                        },
                      }}
                    />
                  </div>

                  <div className={`${baseStyles.grid} ${baseStyles.gridCol3} ${baseStyles.gap16px}`}>
                    <NumberInput
                      label="Years of Residence"
                      id={`additional_owners.${index}.signer_residence_years`}
                      name={`additional_owners.${index}.signer_residence_years`}
                      min={0}
                      max={99}
                      value={owner.signer_residence_years}
                      onChange={(value) => setFieldValue(`additional_owners.${index}.signer_residence_years`, value)}
                      suffix="Years"
                      error={errors?.additional_owners?.[index]?.signer_residence_years}
                      touched={touched?.additional_owners?.[index]?.signer_residence_years}
                      errored={errors?.additional_owners?.[index]?.signer_residence_years}
                    />

                    <NumberInput
                      label="Months of Residence"
                      id={`additional_owners.${index}.signer_residence_months`}
                      name={`additional_owners.${index}.signer_residence_months`}
                      min={0}
                      max={11}
                      value={owner.signer_residence_months}
                      onChange={(value) => setFieldValue(`additional_owners.${index}.signer_residence_months`, value)}
                      suffix="Months"
                      error={errors?.additional_owners?.[index]?.signer_residence_months}
                      touched={touched?.additional_owners?.[index]?.signer_residence_months}
                      errored={errors?.additional_owners?.[index]?.signer_residence_months}
                    />
                  </div>


                  {/* ID Document Section */}
                  <div className={styles.documentSection}>
                    <HeadingWithLine text="IDENTITY DOCUMENT" margin={false} />

                    <div className={`${baseStyles.grid} ${baseStyles.gridCol3} ${baseStyles.gap16px}`} >
                      <div>
                        <Select
                          label="ID Type"
                          placeholder="ID Type"
                          id={`additional_owners.${index}.signer_document_type`}
                          name={`additional_owners.${index}.signer_document_type`}
                          options={idTypeOptions}
                          value={owner.signer_document_type}
                          onChange={(value) => {
                            handleChange({ target: { name: `additional_owners.${index}.signer_document_type`, value } });
                          }}
                          error={errors?.additional_owners?.[index]?.signer_document_type}
                          touched={touched?.additional_owners?.[index]?.signer_document_type}
                          errored={errors?.additional_owners?.[index]?.signer_document_type}
                        />
                      </div>

                      <TextInput
                        label={"ID Number"}
                        placeholder={"ID Number"}
                        type="text"
                        id={`additional_owners.${index}.signer_drivers_license_number`}
                        name={`additional_owners.${index}.signer_drivers_license_number`}
                        value={owner.signer_drivers_license_number}
                        onChange={handleChange}
                        touched={touched?.additional_owners?.[index]?.signer_drivers_license_number}
                        errored={errors?.additional_owners?.[index]?.signer_drivers_license_number}
                        error={errors?.additional_owners?.[index]?.signer_drivers_license_number}
                      />

                      {(owner.signer_document_type === "PASSPORT") && (
                        <div>
                          <Select
                            label="Country"
                            placeholder="Select Country"
                            id={`additional_owners.${index}.signer_document_country`}
                            name={`additional_owners.${index}.signer_document_country`}
                            options={countries}
                            search
                            value={owner.signer_document_country || ""}
                            onChange={(value) => {
                              setFieldValue(`additional_owners.${index}.signer_document_country`, value);
                            }}
                            error={errors?.additional_owners?.[index]?.signer_document_country}
                            touched={touched?.additional_owners?.[index]?.signer_document_country}
                            errored={errors?.additional_owners?.[index]?.signer_document_country}
                          />
                        </div>
                      )}

                      {(owner.signer_document_type === "LOCAL_STATE_ID" || owner.signer_document_type === "STATE_ID") && (
                        <div>
                          <Select
                            label="State"
                            placeholder="Select State"
                            id={`additional_owners.${index}.signer_drivers_license_state`}
                            name={`additional_owners.${index}.signer_drivers_license_state`}
                            options={usStateList}
                            search
                            value={owner.signer_drivers_license_state || ""}
                            onChange={(value) => {
                              setFieldValue(`additional_owners.${index}.signer_drivers_license_state`, value);
                            }}
                            error={errors?.additional_owners?.[index]?.signer_drivers_license_state}
                            touched={touched?.additional_owners?.[index]?.signer_drivers_license_state}
                            errored={errors?.additional_owners?.[index]?.signer_drivers_license_state}
                          />
                        </div>
                      )}

                    </div>

                    <div className={`${baseStyles.grid} ${baseStyles.gridCol2} ${baseStyles.gap16px}`} >
                      <TextInput
                        label={"Social Security Number"}
                        placeholder={"XXXXXXXXX"}
                        type="text"
                        id={`additional_owners.${index}.signer_social_security`}
                        name={`additional_owners.${index}.signer_social_security`}
                        value={owner.signer_social_security}
                        onChange={handleChange}
                        error={errors?.additional_owners?.[index]?.signer_social_security}
                        touched={touched?.additional_owners?.[index]?.signer_social_security}
                        errored={errors?.additional_owners?.[index]?.signer_social_security}
                        cleaveOptions={{
                          blocks: [3, 2, 4],
                          delimiter: "",
                          numericOnly: true,
                          onValueChanged: (e) => {
                            handleChange({
                              target: { name: `additional_owners.${index}.signer_social_security`, value: e.target.value },
                            });
                          },
                        }}
                      />

                      <TextInput
                        label={"Date of Birth"}
                        placeholder={"MM/DD/YYYY"}
                        type="text"
                        id={`additional_owners.${index}.signer_date_of_birth`}
                        name={`additional_owners.${index}.signer_date_of_birth`}
                        value={owner.signer_date_of_birth}
                        error={errors?.additional_owners?.[index]?.signer_date_of_birth}
                        touched={touched?.additional_owners?.[index]?.signer_date_of_birth}
                        errored={errors?.additional_owners?.[index]?.signer_date_of_birth}
                        suffix={<InfoIcon />}
                        cleaveOptions={{
                          date: true,
                          datePattern: ["m", "d", "Y"],
                          delimiter: "/",
                          blocks: [2, 2, 4],
                          onValueChanged: (e) => {
                            handleChange({
                              target: { name: `additional_owners.${index}.signer_date_of_birth`, value: e.target.value },
                            });
                          },
                        }}
                      />
                    </div>
                  </div>

                  {/* Ownership Section */}
                  <div className={styles.documentSection}>
                    <HeadingWithLine text="OWNERSHIP" margin={false} />

                    <div className={`${baseStyles.grid} ${baseStyles.gridCol3} ${baseStyles.gap16px}`} >                      
                      <NumberInput
                        label="Ownership Percentage"
                        placeholder="0"
                        type="text"
                        id={`additional_owners.${index}.signer_ownership_percentage`}
                        name={`additional_owners.${index}.signer_ownership_percentage`}
                        min={0}
                        max={100}
                        value={owner.signer_ownership_percentage}
                        onChange={(value) => setFieldValue(`additional_owners.${index}.signer_ownership_percentage`, value)}
                        suffix={<>
                          <InfoIcon />
                          <span className={baseStyles.ml8px}>%</span>
                        </>}
                        error={errors?.additional_owners?.[index]?.signer_ownership_percentage}
                        touched={touched?.additional_owners?.[index]?.signer_ownership_percentage}
                        errored={errors?.additional_owners?.[index]?.signer_ownership_percentage}
                      />
                    </div>
                  </div>

                </div>
              )}
            </div>
          ))
        }

        {/* Add Owner Button */}
        <div className={styles.addOwnerButton}>
          <Button onClick={addOwner}
            variant={'ghost'}
            icon={<PlusIcon color={'#4C70EF'} />}
            type="button"
          >
            Add Owner
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PersonalInformation; 